/**
 * L'ÉCHANGE DE DEUX AFFECTATIONS, côté navigateur (26/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « En posant sur une autre personne, qu'il y ait un échange entre les
 *   postes. »                                              — RQ, 26/10/2026
 *
 * Ce module dit, PENDANT LE GLISSÉ, si déposer une pastille sur une autre est
 * permis — pour griser les cases refusées avant qu'on n'y aille, au lieu de
 * laisser déposer puis refuser. Le SERVEUR revérifie tout (`POST
 * ged/rh/affectations/echanger`) : ceci ne fait qu'éviter un geste voué au
 * refus.
 *
 * ── Deux implémentations d'une même règle, et ce qui les tient ensemble ────
 *
 * `refusEchange` est le pendant exact de `backend/src/rh-echange.ts`. Deux
 * copies d'une règle divergent au premier ajout — et le glissé, chemin le plus
 * rapide, finirait par autoriser ce que le serveur refuse, ou l'inverse.
 * `tests/front-echange-planning.test.js` joue les MÊMES cas sur les deux et
 * exige les mêmes messages : le jour où l'une bouge seule, il échoue.
 *
 * `verdictLocal` — « qui peut tenir ce poste » — vivait dans `Rh.tsx` et sert le
 * dialogue d'affectation et le glissé de la semaine. Il est ICI pour que
 * l'échange du mois utilise la MÊME fonction, jamais une troisième.
 *
 * Module PUR : aucun import d'exécution.
 */

/* ── « Qui peut tenir ce poste » ─────────────────────────────────────────── */

type Exigence = string | { code: string; niveauMin?: string | null };

/**
 * Le verdict d'habilitation d'une personne sur un poste, depuis ce qu'elle
 * DÉTIENT (`rhDetenues`, `ged-habilitation-personne`) — et non plus depuis
 * `settings.habilitations`, matrice en cours de retrait dont les axes sont des
 * codes de poste périmés depuis le renommage des paillasses.
 *
 * ⚠️ La forme CHAÎNE reste lue, parce que rien ne garantit qu'aucun poste n'en
 * porte plus jamais : une exigence qu'on ne sait pas évaluer est déclarée
 * `indetermine` et laissée au serveur, jamais comptée comme satisfaite.
 */
export function verdictLocal(
  personne: { orgId: string },
  poste: { habilitationsRequises?: Exigence[] | null },
  detenuesParPersonne: Map<string, Map<string, string>>,
): { manquantes: string[]; enFormation: string[]; indetermine: string[]; ok: boolean } {
  const indetermine: string[] = [];
  const exigees = poste.habilitationsRequises || [];
  if (!exigees.length) return { manquantes: [], enFormation: [], indetermine: [], ok: true };
  const siennes = detenuesParPersonne.get(personne.orgId) || new Map<string, string>();
  const manquantes: string[] = [];
  const enFormation: string[] = [];
  for (const e of exigees) {
    // L'ancien modèle — une exigence qui nomme une discipline — n'est plus
    // évaluable depuis le retrait de la matrice. On ne la compte NI comme
    // satisfaite ni comme manquante : le serveur tranche, et on le dit.
    if (typeof e === 'string') { indetermine.push(e); continue; }
    const niveau = siennes.get(e.code);
    if (niveau === undefined) { manquantes.push(e.code); continue; }
    // « En formation » n'est JAMAIS une habilitation : c'est l'état de qui
    // apprend encore et ne doit pas tenir le poste seul.
    if (niveau === 'formation') enFormation.push(e.code);
  }
  // `indetermine` ne bloque PAS l'aperçu : il signale que seul le serveur sait.
  return { manquantes, enFormation, indetermine, ok: !manquantes.length && !enFormation.length };
}

/* ── L'échange ───────────────────────────────────────────────────────────── */

export interface CaseEchange {
  documentId: string;
  date: string;
  posteCode: string;
  orgId: string;
  nature?: string | null;
  derogation?: unknown;
  derogationAbsence?: unknown;
}

export interface LigneDuJour { documentId: string; posteCode: string; orgId: string; date?: string }

const present = (v: unknown): boolean => {
  if (v === null || v === undefined || v === '') return false;
  if (typeof v === 'object') return Object.keys(v as object).length > 0;
  return true;
};

/** Pendant exact de `refusEchange` du serveur — mêmes cas, mêmes messages. */
export function refusEchange(
  a: CaseEchange, b: CaseEchange, jour: LigneDuJour[],
  /** Absence (validée ou demandée) de `orgId` à `date` — lue seulement d'un jour à l'autre. */
  absentLe?: (orgId: string, date: string) => boolean,
): string | null {
  if (a.documentId === b.documentId) return 'Une affectation ne s’échange pas avec elle-même.';
  const memeJour = a.date === b.date;
  if (a.orgId === b.orgId) return 'C’est la même personne : il n’y a rien à échanger.';
  if (memeJour && a.posteCode === b.posteCode) return 'Les deux affectations sont sur le même poste : il n’y a rien à échanger.';
  if (!memeJour && a.posteCode !== b.posteCode) {
    return 'D’un jour à l’autre, l’échange se fait sur le même poste (deux dimanches de garde, par exemple) : ces deux affectations ne sont ni le même jour, ni sur le même poste.';
  }
  for (const x of [a, b]) {
    if ((x.nature || 'travail') !== 'travail') {
      return 'Une journée en formation ou en astreinte ne s’échange pas ici : le tuteur et la nature suivent la personne, pas la case. Passez par la case elle-même.';
    }
    if (present(x.derogation) || present(x.derogationAbsence)) {
      return 'Une affectation qui porte une dérogation ne s’échange pas : la dérogation vaut pour une personne sur un poste, elle ne se transmet pas.';
    }
  }
  /* `cible` est la case que `qui` reprend : son poste, à SA date. */
  const dejaLa = (qui: CaseEchange, cible: CaseEchange) => (jour || []).some(
    (l) => l.orgId === qui.orgId && l.posteCode === cible.posteCode && l.documentId !== cible.documentId
      && (l.date === undefined || l.date === cible.date),
  );
  if (dejaLa(a, b) || dejaLa(b, a)) {
    return 'L’une des deux personnes tient déjà, ce jour-là, le poste qu’elle prendrait : l’échange créerait un doublon.';
  }
  /* Le même jour, les deux sont présentes par construction. D'un jour à l'autre,
     chacune arrive sur une date où elle peut être en congé. */
  if (!memeJour && absentLe && (absentLe(a.orgId, b.date) || absentLe(b.orgId, a.date))) {
    return 'L’une des deux personnes a une absence (validée ou demandée) le jour qu’elle prendrait : l’échange la planifierait pendant son congé.';
  }
  return null;
}

/**
 * LE REMPLACEMENT — une autre personne prend la case (RQ, 01/11/2026 : « en plus
 * du bouton × pour supprimer, un bouton pour placer une autre personne à la
 * place de la personne présente »). Comme l'échange, rien n'est créé ni
 * supprimé : l'affectation CHANGE DE TITULAIRE sur place, créneau et heures
 * gardés — la case n'est jamais vide, même un instant. Une dérogation portée
 * par la case appartenait à l'ancienne titulaire : elle tombe (le journal le
 * dit), la nouvelle doit tenir le poste sans dérogation.
 * Les habilitations ne sont pas ici (comme pour l'échange).
 */
export function refusRemplacement(
  a: CaseEchange, nouveau: string, jour: LigneDuJour[],
  absentLe?: (orgId: string, date: string) => boolean,
): string | null {
  if (!nouveau) return 'Choisissez la personne qui prend la case.';
  if (nouveau === a.orgId) return 'C’est déjà cette personne qui tient la case.';
  if ((a.nature || 'travail') === 'formation') {
    return 'Une journée en formation ne se remplace pas ici : le tuteur et l’apprentissage suivent la personne. Retirez-la, puis affectez la remplaçante.';
  }
  const doublon = (jour || []).some((l) => l.orgId === nouveau && l.posteCode === a.posteCode
    && l.documentId !== a.documentId && (l.date === undefined || l.date === a.date));
  if (doublon) return 'Cette personne tient déjà ce poste ce jour-là : la placer ici créerait un doublon.';
  if (absentLe && absentLe(nouveau, a.date)) {
    return 'Cette personne a une absence (validée ou demandée) ce jour-là.';
  }
  return null;
}

export interface PosteEchange {
  libelle?: string;
  actif?: boolean;
  habilitationsRequises?: Exigence[] | null;
}

/**
 * L'échange est-il permis ? Règle structurelle, puis habilitations dans les DEUX
 * sens. `detenues` vide (non chargées) : on ne bloque pas — « on ne sait pas »
 * n'est pas « non », et le serveur tranche.
 */
export function echangePossible(
  a: CaseEchange, b: CaseEchange,
  jour: LigneDuJour[],
  postes: Map<string, PosteEchange>,
  detenues: Map<string, Map<string, string>>,
  nomDe: (orgId: string) => string,
  absentLe?: (orgId: string, date: string) => boolean,
): { ok: boolean; motif?: string } {
  const refus = refusEchange(a, b, jour, absentLe);
  if (refus) return { ok: false, motif: refus };
  const pa = postes.get(a.posteCode); const pb = postes.get(b.posteCode);
  if (!pa || !pb) return { ok: false, motif: 'Poste inconnu.' };
  if (pa.actif === false || pb.actif === false) return { ok: false, motif: 'L’un des deux postes est désactivé.' };
  if (detenues.size) {
    for (const [qui, poste] of [[a.orgId, pb], [b.orgId, pa]] as const) {
      const v = verdictLocal({ orgId: qui }, poste, detenues);
      if (!v.ok) {
        const manque = [...v.manquantes, ...v.enFormation.map((c) => `${c} (en formation)`)].join(', ');
        return { ok: false, motif: `${nomDe(qui)} n’est pas habilité(e) sur « ${poste.libelle || '?'} » — il manque : ${manque}.` };
      }
    }
  }
  return { ok: true };
}

/* ── L'échange déclaré après coup — une semaine au plus (RQ, 03/11/2026) ──
   Copie de `backend/src/rh-echange.ts`, tenue par `front-echange-planning`. */

/** Jours de recul permis pour une demande d'échange. */
export const RECUL_ECHANGE_JOURS = 7;

/** Le premier jour (AAAA-MM-JJ) qu'une demande d'échange peut encore viser. */
export function limiteEchangeApresCoup(aujourdhui: string): string {
  const d = new Date(`${aujourdhui}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - RECUL_ECHANGE_JOURS);
  return d.toISOString().slice(0, 10);
}

/** Le refus, tel que le demandeur le lit : il dit à qui s'adresser. */
export function refusTropAncien(date: string): string {
  const [a, m, j] = date.split('-');
  return `Le ${j}/${m}/${a} remonte à plus d’une semaine : un échange ne se déclare après coup que dans les ${RECUL_ECHANGE_JOURS} jours. `
    + 'Adressez-vous au responsable du planning pour le modifier.';
}
